import { Record as ImmutableRecord } from 'immutable'
import { AuthStateRecord } from 'lib/reducers/auth'
import { GetReturn, Status } from 'lib/rpc'

function isGetReturn<T>(val: unknown): val is GetReturn<T> {
    return (val as GetReturn<T>).status !== undefined
}

// rpcCacheProxy - provide a reducer of type Record<T> to have it cache
// if items in record are of type GetReturn optionally provide a list of RPC Status list to cache only
// on those status items if left empty everything is cached.
const rpcCacheProxy = <T extends AuthStateRecord, R extends ImmutableRecord<T>>(
    initialData: R,
    ...cacheableStatuses: Status[]
): (() => R) => {
    let cachedData = initialData

    const setter =
        (
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            receiver: any,
        ) =>
        <K extends keyof T>(key: K, value: T[K]): R => {
            if (isGetReturn<T[K]>(value)) {
                if (cacheableStatuses.length && !cacheableStatuses.includes(value.status)) {
                    return receiver
                }
            }
            cachedData = cachedData.set(key, value)
            return new Proxy<R>(cachedData, handler)
        }
    const getter = <K extends keyof T>(key: K): T[K] => {
        return cachedData.get(key)
    }
    const handler = {
        // any's here are the actual types of a proxy getter
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        get(target: ImmutableRecord<T>, p: string | symbol, receiver: any): any {
            switch (p) {
                case 'set':
                    return setter(receiver)
                case 'get':
                    return getter
                default:
                    return target[p]
            }
        },
    }
    return (): R => {
        return new Proxy<R>(initialData, handler)
    }
}
export default rpcCacheProxy
